import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import { trackerList, trackerAdd, trackerDelete } from "../api";

// Shared client-side tracker state so the in-context "Track" buttons (Today's
// Card, Trainer/Jockey Explorers) know what's already tracked without each
// button hitting the API. Loads the user's list once on login and mutates the
// local map optimistically on track/untrack. Logged-out → empty map, and the
// buttons render a "log in to track" affordance instead.

const TrackerContext = createContext(null);

const keyOf = (type, id) => `${type}:${id}`;

export function useTracker() {
  const ctx = useContext(TrackerContext);
  // Defensive default so a button rendered outside the provider degrades to
  // "logged-out" behaviour rather than throwing.
  return (
    ctx || {
      ready: true,
      isTracked: () => false,
      track: async () => {
        throw new Error("Tracker unavailable");
      },
      untrack: async () => {},
    }
  );
}

export function TrackerProvider({ children }) {
  const { user } = useAuth();
  // key -> { entryId, active }
  const [entries, setEntries] = useState({});
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let live = true;
    (async () => {
      if (!user) {
        if (live) {
          setEntries({});
          setReady(true);
        }
        return;
      }
      setReady(false);
      try {
        const data = await trackerList();
        const map = {};
        const groups = data?.entities || {};
        Object.values(groups).forEach((list) => {
          (list || []).forEach((e) => {
            map[keyOf(e.entity_type, e.entity_id)] = {
              entryId: e.id,
              active: !!e.active,
            };
          });
        });
        if (live) setEntries(map);
      } catch {
        // A tracker-list failure must never break the pages the buttons live
        // on; treat as "nothing tracked" and let track() surface real errors.
        if (live) setEntries({});
      } finally {
        if (live) setReady(true);
      }
    })();
    return () => {
      live = false;
    };
  }, [user]);

  const isTracked = useCallback(
    (type, id) => {
      const e = entries[keyOf(type, id)];
      return !!(e && e.active);
    },
    [entries]
  );

  const track = useCallback(async ({ entity_type, entity_id, entity_name }) => {
    // Throws on 409 (cap) so the button can show the limit message.
    const res = await trackerAdd({ entity_type, entity_id, entity_name });
    const entryId = res?.entry?.id;
    setEntries((prev) => ({
      ...prev,
      [keyOf(entity_type, entity_id)]: { entryId, active: true },
    }));
    return res;
  }, []);

  const untrack = useCallback(
    async (type, id) => {
      const k = keyOf(type, id);
      const e = entries[k];
      if (!e?.entryId) return;
      await trackerDelete(e.entryId);
      setEntries((prev) => {
        const next = { ...prev };
        delete next[k];
        return next;
      });
    },
    [entries]
  );

  const value = { ready, isTracked, track, untrack, loggedIn: !!user };

  return (
    <TrackerContext.Provider value={value}>{children}</TrackerContext.Provider>
  );
}
